import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import SearchBar from '@tls/ui-search-bar';
import { stringifiedSpinner } from '@tls/ui-spinner';
import { NumberRenderer } from '@tls/ui-numeral';

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}

const alignLeftCellStyle = {
  cellStyle: {
    textAlign: 'left'
  }
};
const alignRightCellStyle = {
  cellStyle: {
    textAlign: 'right'
  }
};
const alignCenterCellStyle = {
  cellStyle: {
    textAlign: 'center'
  }
};

class AgGridWithCount extends PureComponent {
  constructor() {
    super(...arguments);
    this.state = {
      first: 0,
      last: 0,
      count: 0
    };
    this.updateCount = debounce(params => {
      const {
        top,
        bottom
      } = params.api.getVerticalPixelRange();
      const firstRowId = params.api.getFirstDisplayedRow();
      const lastRowId = params.api.getLastDisplayedRow();
      const displayedRows = Array.from(Array(lastRowId - firstRowId + 1).keys());
      const firstDispayedRow = displayedRows.find(id => {
        const index = id + firstRowId;
        const row = params.api.getDisplayedRowAtIndex(index);
        return row.rowTop + row.rowHeight >= top;
      });
      const lastDisplayedRow = displayedRows.sort((idA, idB) => idB - idA).find(id => {
        const index = id + firstRowId;
        const row = params.api.getDisplayedRowAtIndex(index);
        return row.rowTop < bottom;
      });
      this.setState(() => ({
        first: firstDispayedRow + firstRowId + 1 || 0,
        last: lastDisplayedRow + firstRowId + 1 || 0,
        count: params.api.getDisplayedRowCount()
      }));
    }, 100);
    this.onBodyScroll = params => {
      const {
        onBodyScroll
      } = this.props;
      if (onBodyScroll) {
        onBodyScroll(params);
      }
      this.updateCount(params);
    };
    this.onFilterChanged = params => {
      const {
        onFilterChanged
      } = this.props;
      if (onFilterChanged) {
        onFilterChanged(params);
      }
      this.updateCount(params);
    };
    this.onGridReady = params => {
      const {
        onGridReady
      } = this.props;
      if (onGridReady) {
        onGridReady(params);
      }
      this.updateCount(params);
    };
    this.onGridSizeChanged = params => {
      const {
        onGridSizeChanged
      } = this.props;
      if (onGridSizeChanged) {
        onGridSizeChanged(params);
      }
      this.updateCount(params);
    };
    this.onRowDataUpdated = params => {
      const {
        onRowDataUpdated
      } = this.props;
      if (onRowDataUpdated) {
        onRowDataUpdated(params);
      }
      this.updateCount(params);
    };
  }
  render() {
    const {
      first,
      last,
      count
    } = this.state;
    return /*#__PURE__*/React.createElement("div", {
      style: {
        height: '100%'
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        height: 'calc(100% - 40px)'
      }
    }, /*#__PURE__*/React.createElement(AgGridReact, _extends({}, this.props, {
      onBodyScroll: this.onBodyScroll,
      onFilterChanged: this.onFilterChanged,
      onGridReady: this.onGridReady,
      onGridSizeChanged: this.onGridSizeChanged,
      onRowDataUpdated: this.onRowDataUpdated
    }))), /*#__PURE__*/React.createElement("div", {
      className: "pt-4 text-right grid-count"
    }, /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("span", {
      className: "font-weight-bold"
    }, first), " -", ' ', /*#__PURE__*/React.createElement("span", {
      className: "font-weight-bold"
    }, last), " of", ' ', /*#__PURE__*/React.createElement("span", {
      className: "font-weight-bold"
    }, /*#__PURE__*/React.createElement(NumberRenderer, {
      value: count
    })))));
  }
}
AgGridWithCount.defaultProps = {
  onBodyScroll: null,
  onFilterChanged: null,
  onGridReady: null,
  onGridSizeChanged: null,
  onRowDataUpdated: null
};

const selectionStateSelector = _ref => {
  let {
    agGrid
  } = _ref;
  return agGrid.agSelectionState;
};
const rowSelectionStateSelector = _ref2 => {
  let {
    agGrid
  } = _ref2;
  return agGrid.agRowSelectionState;
};

const SELECT_ROW = '@tls/ui-aggrid/SELECT_ROW';
const SELECT_ROW_BY_ROW_INDEX = '@tls/ui-aggrid/SELECT_ROW_BY_ROW_INDEX';
const RESET_SELECTION_STATE = '@tls/ui-aggrid/RESET_SELECTION_STATE';

const selectRow = _ref => {
  let {
    name,
    fields,
    data
  } = _ref;
  const values = fields.reduce((result, field) => ({
    ...result,
    [field]: data[field]
  }), {});
  return {
    type: SELECT_ROW,
    payload: {
      name,
      fields,
      values
    }
  };
};
const selectRowByRowIndex = rowIndex => ({
  type: SELECT_ROW_BY_ROW_INDEX,
  rowIndex
});
const resetSelectionState = () => ({
  type: RESET_SELECTION_STATE
});

const toString = data => {
  if (data === null) {
    return '';
  }
  if (typeof data === 'boolean' || typeof data === 'number') {
    return data.toString();
  }
  if (typeof data === 'string') {
    return data;
  }
  if (typeof data === 'symbol') {
    throw new TypeError();
  }
  return JSON.stringify(data);
};
const equalNodes = (node, selectionState) => selectionState.fields.every(field => node.data[field] === selectionState.values[field]);
const compareCollator = new Intl.Collator('en', {
  sensitivity: 'accent'
});
const comparator = (valueA, valueB) => {
  if (typeof valueA === 'undefined' && typeof valueB === 'undefined') {
    return 0;
  }
  if (typeof valueA === 'undefined') {
    return 1;
  }
  if (typeof valueB === 'undefined') {
    return -1;
  }
  if (typeof valueA === 'number' && typeof valueB === 'number') {
    return valueA - valueB;
  }
  const aString = toString(valueA);
  const bString = toString(valueB);
  return compareCollator.compare(aString, bString);
};

const defaultRowHeight = 25;
const getRowHeightBasedOnContent = _ref => {
  let {
    field,
    margins,
    lineHeight,
    extraLength,
    averageCharsInLine
  } = _ref;
  return params => params.data.field ? margins + lineHeight * Math.ceil((params.data[field].length + extraLength) / averageCharsInLine) : defaultRowHeight;
};
const excludeFromFilteringColumnProperty = {
  getQuickFilterText() {
    return '';
  }
};
const normalizeRowSelection = rowSelection => {
  if (!rowSelection) {
    return rowSelection;
  }
  const normalizedRowSelection = {
    ...rowSelection
  };
  if (normalizedRowSelection.mode === 'single') {
    normalizedRowSelection.mode = 'singleRow';
  }
  if (normalizedRowSelection.mode === 'multiple') {
    normalizedRowSelection.mode = 'multiRow';
  }
  return normalizedRowSelection;
};
const sizeColumnsToFit = (gridApi, suppressSizeColumnsToFit) => {
  if (suppressSizeColumnsToFit) {
    return;
  }
  gridApi.sizeColumnsToFit();
};
const syncLoadingOverlay = function (gridApi, isLoading) {
  let rowData = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  if (!gridApi) {
    return;
  }
  if (isLoading) {
    if (typeof gridApi.setGridOption === 'function') {
      gridApi.setGridOption('loading', true);
      return;
    }
    if (typeof gridApi.showLoadingOverlay === 'function') {
      gridApi.showLoadingOverlay();
    }
    return;
  }
  if (typeof gridApi.setGridOption === 'function') {
    gridApi.setGridOption('loading', false);
  }
  if ((rowData === null || rowData === void 0 ? void 0 : rowData.length) === 0) {
    if (typeof gridApi.showNoRowsOverlay === 'function') {
      gridApi.showNoRowsOverlay();
    }
    return;
  }
  if (typeof gridApi.hideOverlay === 'function') {
    gridApi.hideOverlay();
  }
};
class AgGrid extends PureComponent {
  constructor(props) {
    super(props);
    this.componentDidUpdate = prevProps => {
      if (this.gridApi) {
        if (prevProps.columnDefs !== this.props.columnDefs) {
          // AG Grid React automatically handles columnDefs prop changes
          // No need to manually call setColumnDefs (which doesn't exist in AG Grid API)
          // The grid will automatically update when the columnDefs prop changes
          // We only need to adjust column sizing after the update
          sizeColumnsToFit(this.gridApi, this.props.suppressSizeColumnsToFit);
        }
        if (prevProps.isGridLoading !== this.props.isGridLoading) {
          syncLoadingOverlay(this.gridApi, this.props.isGridLoading, this.props.rowData);
        }
      }
    };
    this.componentWillUnmount = () => {
      this.props.resetSelectionState();
    };
    this.componentSizeColumnsToFit = params => {
      const {
        suppressSizeColumnsToFit
      } = this.props;
      sizeColumnsToFit(params.api, suppressSizeColumnsToFit);
    };
    this.refreshScrollPagination = _ref2 => {
      let {
        api
      } = _ref2;
      if (this.props.withScrollPagination) {
        const firstRow = api.getFirstDisplayedRow();
        const lastRow = api.getLastDisplayedRow();
        this.setState({
          firstRow: lastRow < 0 ? 0 : firstRow + 1,
          lastRow: lastRow < 0 ? 0 : lastRow + 1,
          rowCount: api === null || api === void 0 ? void 0 : api.getDisplayedRowCount()
        });
      }
    };
    this.onGridReady = params => {
      const {
        gridSelectionState,
        resetSelectionState,
        suppressSizeColumnsToFit
      } = this.props;
      this.gridApi = params.api;
      this.columnApi = params.columnApi;
      sizeColumnsToFit(params.api, suppressSizeColumnsToFit);
      if (this.props.isGridLoading) {
        syncLoadingOverlay(this.gridApi, true, this.props.rowData);
      }
      if (gridSelectionState && this.gridApi) {
        this.gridApi.forEachNode(node => {
          if (equalNodes(node, gridSelectionState)) {
            node.setSelected(true, true);
            this.gridApi.ensureNodeVisible(node, 'top');
            resetSelectionState();
          }
        });
      }
      if (this.props.onGridReady) {
        this.props.onGridReady(params);
      }
      if (this.props.absoluteScroll) {
        this.selectRowDebounced = debounce(() => {
          const {
            rowIndex
          } = this.gridApi.getFocusedCell();
          this.gridApi.getDisplayedRowAtIndex(rowIndex).setSelected(true);
        }, 300);
      }
    };
    this.onRowDataUpdated = params => {
      this.gridApi = params.api;
      this.manageSelectionState(params);
      this.selectFirstRow(params.api);
      if (this.props.isGridLoading) {
        syncLoadingOverlay(params.api, true, this.props.rowData);
      }
      if (this.props.onRowDataUpdated) {
        this.props.onRowDataUpdated(params);
      }
      this.refreshScrollPagination(params);
    };
    this.onGridColumnsChanged = params => {
      if (this.props.isGridLoading) {
        syncLoadingOverlay(params.api, true, this.props.rowData);
      }
      if (this.props.onGridColumnsChanged) {
        this.props.onGridColumnsChanged(params);
      }
    };
    this.onViewportChanged = params => {
      if (this.props.isGridLoading) {
        syncLoadingOverlay(params.api, true, this.props.rowData);
      }
      if (this.props.onViewportChanged) {
        this.props.onViewportChanged(params);
      }
      this.refreshScrollPagination(params);
    };
    this.onFilterChanged = params => {
      if (this.props.isGridLoading) {
        syncLoadingOverlay(params.api, true, this.props.rowData);
      }
      if (this.props.onFilterChanged) {
        this.props.onFilterChanged(params);
      }
      this.refreshScrollPagination(params);
    };
    this.defaultColumnDefs = {
      menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
      comparator,
      sortable: true,
      filter: true,
      suppressHeaderFilterButton: true,
      resizable: true,
      ...alignCenterCellStyle,
      ...this.props.defaultColDef
    };
    this.getMainMenuItems = params => {
      const mainMenuItemsWithoutToolPanel = params.defaultItems.slice(0, -1);
      return mainMenuItemsWithoutToolPanel;
    };
    this.reset = () => {
      if (this.gridApi) {
        this.gridApi.setFilterModel(null);
        if (typeof this.gridApi.resetColumnState === 'function') {
          this.gridApi.resetColumnState();
        }
      }
      this.unsetFilter();
      if (this.props.onReset) {
        this.props.onReset();
      }
    };
    this.setFilter = filter => {
      this.setState({
        filter
      });
    };
    this.unsetFilter = () => {
      this.setState({
        filter: ''
      });
    };
    this.manageSelectionState = _ref3 => {
      let {
        api
      } = _ref3;
      const {
        gridSelectionState,
        rowSelectionState,
        resetSelectionState,
        suppressSizeColumnsToFit
      } = this.props;
      sizeColumnsToFit(api, suppressSizeColumnsToFit);
      if (rowSelectionState && this.gridApi) {
        this.gridApi.forEachNode(node => {
          if (node.rowIndex === rowSelectionState) {
            node.setSelected(true, true);
            this.gridApi.ensureNodeVisible(node, 'top');
            resetSelectionState();
          }
        });
        return;
      }
      if (gridSelectionState && this.gridApi) {
        this.gridApi.forEachNode(node => {
          if (equalNodes(node, gridSelectionState)) {
            node.setSelected(true, true);
            this.gridApi.ensureNodeVisible(node, 'top');
            resetSelectionState();
          }
        });
      }
    };
    this.selectFirstRow = api => {
      if (this.props.autoSelectFirstRowOnFirstLoad) {
        if (this.props.rowData.length === 0 || this.state.dataAlreadyLoadedOnce) {
          return;
        }
        this.setState({
          dataAlreadyLoadedOnce: true
        });
        api.forEachNode(node => {
          if (node.childIndex === 0) {
            node.setSelected(true, true);
            api.ensureNodeVisible(node, 'top');
            this.props.resetSelectionState();
          }
        });
      }
    };
    this.createCustomContextMenu = params => this.props.createCustomContextMenu ? this.props.createCustomContextMenu(params) : ['copy', 'copyWithHeaders', 'separator', {
      name: 'Export',
      subMenu: ['csvExport', {
        name: 'Excel Export',
        action: () => this.gridApi.exportDataAsExcel()
      }]
    }];
    this.handleKeypress = event => {
      if (this.props.absoluteScroll) {
        const {
          columnApi,
          gridApi
        } = this;
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          this.selectRowDebounced();
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          const visibleColumns = columnApi.getAllDisplayedVirtualColumns().filter(column => !column.pinned);
          if (visibleColumns.length === 0) {
            return;
          }
          const leftmostVisibleColumn = visibleColumns[0];
          const rightmostVisibleColumn = visibleColumns[visibleColumns.length - 1];
          if (event.key === 'ArrowRight') {
            const invisibleColumnOnTheRight = columnApi.getDisplayedColAfter(rightmostVisibleColumn);
            if (invisibleColumnOnTheRight && !invisibleColumnOnTheRight.pinned) {
              gridApi.ensureColumnVisible(invisibleColumnOnTheRight.colId);
            }
          } else {
            const invisibleColumnOnTheLeft = columnApi.getDisplayedColBefore(leftmostVisibleColumn);
            if (invisibleColumnOnTheLeft && !invisibleColumnOnTheLeft.pinned) {
              gridApi.ensureColumnVisible(invisibleColumnOnTheLeft.colId);
            }
          }
        }
      }
    };
    this.state = {
      filter: props.initialQuickFilterValue,
      dataAlreadyLoadedOnce: false,
      firstRow: 0,
      lastRow: 0,
      rowCount: 0
    };
  }
  render() {
    const {
      componentToRenderOnSearchBar,
      messageToRenderOnSearchBar,
      withSearchBar,
      withResetIcon,
      SearchBarComponent,
      disableSearchBar,
      showCount,
      allowRowShadowing,
      isRowShadingEnabled,
      isScrollDisabled,
      rowData,
      withScrollPagination,
      ...gridProps
    } = this.props;
    const {
      filter
    } = this.state;
    const searchBarProps = {
      onCancel: this.unsetFilter,
      onSearch: this.setFilter,
      value: filter,
      withResetIcon,
      reset: this.reset
    };
    const defaultRowSelection = typeof gridProps.onSelectionChanged === 'function' ? {
      mode: 'singleRow',
      checkboxes: false,
      headerCheckbox: false,
      enableClickSelection: true
    } : undefined;
    const rowSelectionConfig = normalizeRowSelection(gridProps.rowSelection || defaultRowSelection);
    const rowSelection = rowSelectionConfig ? {
      enableSelectionWithoutKeys: true,
      ...rowSelectionConfig
    } : undefined;
    return /*#__PURE__*/React.createElement("div", {
      className: "ag-grid-custom"
    }, !withSearchBar ? null : /*#__PURE__*/React.createElement("div", {
      className: "row"
    }, !componentToRenderOnSearchBar ? null : /*#__PURE__*/React.createElement("div", {
      className: "col-3"
    }, componentToRenderOnSearchBar), !messageToRenderOnSearchBar ? null : /*#__PURE__*/React.createElement("div", {
      className: "col-6 ml-auto"
    }, messageToRenderOnSearchBar), SearchBarComponent ? /*#__PURE__*/React.createElement(SearchBar, _extends({
      Component: SearchBarComponent
    }, searchBarProps)) : /*#__PURE__*/React.createElement("div", {
      className: classnames({
        'col-3': Boolean(messageToRenderOnSearchBar),
        'col-5': !messageToRenderOnSearchBar,
        'ml-auto': !messageToRenderOnSearchBar
      })
    }, /*#__PURE__*/React.createElement(SearchBar, _extends({}, searchBarProps, {
      disabled: disableSearchBar
    })))), /*#__PURE__*/React.createElement("div", {
      onKeyDown: this.handleKeypress,
      tabIndex: "0",
      className: classnames('ag-theme-bootstrap', {
        'ag-theme-bootstrap-shadowed': allowRowShadowing && isRowShadingEnabled
      }, {
        'ag-grid-disable-scroll': isScrollDisabled
      }, {
        'ag-grid-with-scroll-pagination': withScrollPagination
      })
    }, showCount ? /*#__PURE__*/React.createElement(AgGridWithCount, _extends({
      quickFilterText: filter,
      rowSelection: rowSelection,
      suppressDragLeaveHidesColumns: true
    }, gridProps, {
      defaultColDef: this.defaultColumnDefs,
      getMainMenuItems: this.getMainMenuItems,
      getContextMenuItems: this.createCustomContextMenu,
      onGridReady: this.onGridReady,
      onRowDataUpdated: this.onRowDataUpdated,
      onGridColumnsChanged: this.onGridColumnsChanged,
      onViewportChanged: this.onViewportChanged,
      onFilterChanged: this.onFilterChanged,
      overlayLoadingTemplate: stringifiedSpinner,
      rowData: rowData
      // if show withScrollPagination, it should set row buffer as 0, or the number of rows will be added to the value of rowBuffer.
      ,
      rowBuffer: withScrollPagination ? 0 : gridProps.rowBuffer
    })) : /*#__PURE__*/React.createElement(AgGridReact, _extends({
      quickFilterText: filter,
      rowSelection: rowSelection,
      suppressDragLeaveHidesColumns: true
    }, gridProps, {
      defaultColDef: this.defaultColumnDefs,
      getContextMenuItems: this.createCustomContextMenu,
      getMainMenuItems: this.getMainMenuItems,
      onGridReady: this.onGridReady,
      onRowDataUpdated: this.onRowDataUpdated,
      onGridColumnsChanged: this.onGridColumnsChanged,
      onViewportChanged: this.onViewportChanged,
      onFilterChanged: this.onFilterChanged,
      overlayLoadingTemplate: stringifiedSpinner,
      rowData: rowData
      // if show withScrollPagination, it should set row buffer as 0, or the number of rows will be added to the value of rowBuffer.
      ,
      rowBuffer: withScrollPagination ? 0 : gridProps.rowBuffer
    })), withScrollPagination && /*#__PURE__*/React.createElement("p", {
      className: "scroll-pagination"
    }, "Rows: ", /*#__PURE__*/React.createElement("b", null, this.state.firstRow), "-", /*#__PURE__*/React.createElement("b", null, this.state.lastRow), " of", ' ', /*#__PURE__*/React.createElement("b", null, this.state.rowCount))));
  }
}
AgGrid.defaultProps = {
  headerHeight: 40,
  rowHeight: defaultRowHeight,
  onRowDataUpdated: null,
  onGridColumnsChanged: null,
  showCount: false,
  suppressCellFocus: true,
  withSearchBar: false,
  withResetIcon: false,
  SearchBarComponent: null,
  disableSearchBar: false,
  allowRowShadowing: false,
  componentToRenderOnSearchBar: null,
  messageToRenderOnSearchBar: null,
  suppressSizeColumnsToFit: false,
  isScrollDisabled: false,
  autoSelectFirstRowOnFirstLoad: false,
  isGridLoading: false,
  absoluteScroll: true,
  isRowShadingEnabled: false,
  initialQuickFilterValue: '',
  withScrollPagination: false,
  onViewportChanged: null,
  onFilterChanged: null
};
var AgGrid$1 = connect(state => ({
  gridSelectionState: selectionStateSelector(state),
  rowSelectionState: rowSelectionStateSelector(state)
}), {
  resetSelectionState
})(AgGrid);

var initialState = {
  agSelectionState: null,
  agRowSelectionState: null
};

function agGridReducer() {
  let state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : initialState;
  let action = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  switch (action.type) {
    case SELECT_ROW:
      return {
        ...state,
        agSelectionState: action.payload
      };
    case SELECT_ROW_BY_ROW_INDEX:
      return {
        ...state,
        agRowSelectionState: action.rowIndex
      };
    case RESET_SELECTION_STATE:
      return {
        ...state,
        agSelectionState: null,
        agRowSelectionState: null
      };
    default:
      return state;
  }
}

export { RESET_SELECTION_STATE, SELECT_ROW, SELECT_ROW_BY_ROW_INDEX, alignCenterCellStyle, alignLeftCellStyle, alignRightCellStyle, comparator, AgGrid$1 as default, equalNodes, excludeFromFilteringColumnProperty, getRowHeightBasedOnContent, initialState, agGridReducer as reducer, resetSelectionState, rowSelectionStateSelector, selectRow, selectRowByRowIndex, selectionStateSelector, sizeColumnsToFit, toString };
